import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbRowRemove, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { queryClient } from '@/clients';
import {
    fetchPrefetchedDetailQuery,
    getPrefetchedDetailQueryData,
    workspaceResourceDetailQueryOptions,
} from '@/queries/tanstack/prefetch';
import { folderService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { WorkspaceFolderGlyph } from '@/shared/components/workspace-resources';
import {
    appendEntity,
    createSingleFlightKey,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { Folder } from '@/types';

import {
    addFolderResources,
    captureAddedFolderResourceIds,
    captureRemovedFolderResources,
    removeFolderResources,
    restoreFolderResources,
    selectFolderResourceResponse,
} from '../resourceCollections';
import { FolderActions, FolderState } from '../types';

export const createFolderActions: StateCreator<
    FolderState & FolderActions,
    [['zustand/immer', never]],
    [],
    FolderActions
> = (set, get, store) => ({
    // Core
    fetchFolders: async (params) => {
        set({ loading: true });

        try {
            const folders = await folderService.list(params);

            set({ folders, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load folders' });
        }
    },
    fetchFolderById: async (id) => {
        const queryOptions = workspaceResourceDetailQueryOptions.folders(id);
        const cachedFolder = getPrefetchedDetailQueryData(queryClient, queryOptions);

        set({
            loadingFolder: !cachedFolder,
            error: undefined,
            ...(cachedFolder ? { selectedFolder: cachedFolder } : {}),
        });

        try {
            const folder = await fetchPrefetchedDetailQuery(queryClient, queryOptions);

            set({ selectedFolder: folder, loadingFolder: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch folder detail';
                state.loadingFolder = false;
            });
        }
    },
    clearSelectedFolder: () => {
        set((state) => {
            state.selectedFolder = null;
        });
    },
    createFolder: async (input) =>
        runSingleFlight(createSingleFlightKey('folders:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Folder;

            set({ loadingFolder: true, folders: appendEntity(get().folders, optimistic) });

            try {
                const folder = await folderService.create(input);

                set({
                    folders: replaceOptimisticEntityById(get().folders, tempId, folder),
                    loadingFolder: false,
                });

                toast(`Folder Created`, {
                    icon: createElement(WorkspaceFolderGlyph, { size: '1rem', variant: 'create' }),
                    description: folder.name,
                    resource: { id: folder.id, type: 'folder' },
                });

                return folder;
            } catch (error) {
                captureException(error);

                set((state) => {
                    state.folders = state.folders.filter((f) => f.id !== tempId);
                    state.error = 'Failed to create folder';
                    state.loadingFolder = false;
                });
            }
        }),
    updateFolder: async (id, updates) => {
        const prev = get().folders.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set((state) => {
            const f = state.folders.find((f) => f.id === id);
            if (f) {
                Object.assign(f, updates);
            }

            // Also update selectedFolder if it matches
            if (state.selectedFolder?.id === id) {
                Object.assign(state.selectedFolder, updates);
            }
        });

        try {
            const folder = await folderService.update(id, updates);

            set((state) => {
                const f = state.folders.find((f) => f.id === id);
                if (f) {
                    Object.assign(f, folder);
                }

                if (state.selectedFolder?.id === id) {
                    Object.assign(state.selectedFolder, folder);
                }
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const f = state.folders.find((f) => f.id === id);
                if (f) {
                    Object.assign(f, prev);
                }

                if (state.selectedFolder?.id === id) {
                    Object.assign(state.selectedFolder, prev);
                }

                state.error = 'Failed to update folder';
            });
        }
    },
    deleteFolder: async (id) => {
        const prev = get().folders;

        set((state) => {
            state.folders = state.folders.filter((f) => f.id !== id);

            // Clear selectedFolder if it's the deleted one
            if (state.selectedFolder?.id === id) {
                state.selectedFolder = null;
            }
        });

        try {
            await folderService.delete(id);
        } catch (error) {
            captureException(error);

            set({ folders: prev, error: 'Failed to delete folder' });
        }
    },
    applyFolderDelta: (packet) => {
        if (packet.entity !== 'folder') {
            return;
        }

        const next = reduceEntityDelta<Folder>(get().folders, get().selectedFolder, packet);

        if (!next) {
            return;
        }

        set({
            folders: next.items,
            selectedFolder: next.selected,
        });
    },
    // - Aliases
    duplicateFolder: async (id) => {
        const prev = get().folders;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await folderService.duplicate(id);

            set({ folders: upsertEntityById(get().folders, duplicated, { prepend: true }) });

            toast(`Folder Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, type: 'folder' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ folders: prev, error: 'Failed to duplicate folder' });

            toast.error('Failed to duplicate folder');
        }
    },
    toggleFolderFavorite: async (id, favorited) => {
        const prev = get().folders.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const folder = state.folders.find((f) => f.id === id);
            if (folder) {
                folder.favorited = favorited;
            }

            // Also update selectedFolder if it matches
            if (state.selectedFolder?.id === id) {
                state.selectedFolder.favorited = favorited;
            }
        });

        try {
            await folderService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'folder' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const folder = state.folders.find((f) => f.id === id);
                if (folder) {
                    folder.favorited = prev.favorited;
                }

                // Revert selectedFolder favorited if it matches
                if (state.selectedFolder?.id === id) {
                    state.selectedFolder.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleFolderLock: async (id, locked) => {
        const prev = get().folders.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const folder = state.folders.find((f) => f.id === id);
            if (folder) {
                folder.locked = locked;
            }

            // Also update selectedFolder if it matches
            if (state.selectedFolder?.id === id) {
                state.selectedFolder.locked = locked;
            }
        });

        try {
            await folderService.setLocked(id, locked);

            toast(`Folder ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'folder' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const folder = state.folders.find((f) => f.id === id);
                if (folder) {
                    folder.locked = prev.locked;
                }

                // Revert selectedFolder locked if it matches
                if (state.selectedFolder?.id === id) {
                    state.selectedFolder.locked = prev.locked;
                }

                state.error = 'Failed to update locked';
            });
        }
    },
    toggleFolderArchive: async (id, archived) => {
        const prev = get().folders.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const folder = state.folders.find((f) => f.id === id);
            if (folder) {
                folder.archived = archived;
            }

            // Also update selectedFolder if it matches
            if (state.selectedFolder?.id === id) {
                state.selectedFolder.archived = archived;
            }
        });

        try {
            await folderService.setArchived(id, archived);

            toast(`Folder ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'folder' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const folder = state.folders.find((f) => f.id === id);
                if (folder) {
                    folder.archived = prev.archived;
                }

                // Revert selectedFolder archived if it matches
                if (state.selectedFolder?.id === id) {
                    state.selectedFolder.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    addResourcesToFolder: async (folderId, resources) => {
        const listedFolder = get().folders.find((folder) => folder.id === folderId);
        const selectedFolder = get().selectedFolder?.id === folderId ? get().selectedFolder : undefined;

        if (!listedFolder && !selectedFolder) {
            return;
        }

        const listedOptimisticResourceIds = listedFolder ? captureAddedFolderResourceIds(listedFolder, resources) : [];
        const selectedOptimisticResourceIds = selectedFolder
            ? captureAddedFolderResourceIds(selectedFolder, resources)
            : [];

        set((state) => {
            const folderIndex = state.folders.findIndex((folder) => folder.id === folderId);

            if (folderIndex !== -1) {
                Object.assign(
                    state.folders[folderIndex],
                    addFolderResources(state.folders[folderIndex] as Folder, resources),
                );
            }

            if (state.selectedFolder?.id === folderId) {
                Object.assign(state.selectedFolder, addFolderResources(state.selectedFolder as Folder, resources));
            }
        });

        try {
            const updatedFolder = await folderService.addResources(folderId, resources);
            const authoritativeAdditions = selectFolderResourceResponse(updatedFolder, resources);

            set((state) => {
                const folderIndex = state.folders.findIndex((folder) => folder.id === folderId);

                if (folderIndex !== -1) {
                    Object.assign(
                        state.folders[folderIndex],
                        addFolderResources(state.folders[folderIndex] as Folder, authoritativeAdditions),
                    );
                }

                if (state.selectedFolder?.id === folderId) {
                    Object.assign(
                        state.selectedFolder,
                        addFolderResources(state.selectedFolder as Folder, authoritativeAdditions),
                    );
                }
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const folderIndex = state.folders.findIndex((folder) => folder.id === folderId);

                if (folderIndex !== -1) {
                    Object.assign(
                        state.folders[folderIndex],
                        removeFolderResources(state.folders[folderIndex] as Folder, listedOptimisticResourceIds),
                    );
                }

                if (state.selectedFolder?.id === folderId) {
                    Object.assign(
                        state.selectedFolder,
                        removeFolderResources(state.selectedFolder as Folder, selectedOptimisticResourceIds),
                    );
                }

                state.error = 'Failed to add resources to folder';
            });

            throw error;
        }
    },
    removeResourceFromFolder: async (folderId, resourceType, resourceId, itemTitle) => {
        await get().removeResourcesFromFolder(folderId, [{ itemTitle, resourceId, resourceType }]);
    },
    removeResourcesFromFolder: async (folderId, resources) => {
        const uniqueResources = [
            ...new Map(
                resources.map((resource) => [`${resource.resourceType}:${resource.resourceId}`, resource] as const),
            ).values(),
        ];
        const listedFolder = get().folders.find((folder) => folder.id === folderId);
        const selectedFolder = get().selectedFolder?.id === folderId ? get().selectedFolder : undefined;

        if (!uniqueResources.length || (!listedFolder && !selectedFolder)) {
            return;
        }

        const listedRemovedResources = listedFolder ? captureRemovedFolderResources(listedFolder, uniqueResources) : [];
        const selectedRemovedResources = selectedFolder
            ? captureRemovedFolderResources(selectedFolder, uniqueResources)
            : [];

        set((state) => {
            const folderIndex = state.folders.findIndex((folder) => folder.id === folderId);

            if (folderIndex !== -1) {
                Object.assign(
                    state.folders[folderIndex],
                    removeFolderResources(state.folders[folderIndex] as Folder, uniqueResources),
                );
            }

            if (state.selectedFolder?.id === folderId) {
                Object.assign(
                    state.selectedFolder,
                    removeFolderResources(state.selectedFolder as Folder, uniqueResources),
                );
            }
        });

        const results = await Promise.allSettled(
            uniqueResources.map((resource) =>
                folderService.removeResource(folderId, resource.resourceType, resource.resourceId),
            ),
        );
        const failedResources = uniqueResources.filter((_, index) => results[index].status === 'rejected');
        const failedKeys = new Set(
            failedResources.map((resource) => `${resource.resourceType}:${resource.resourceId}`),
        );
        const listedSnapshotsToRestore = listedRemovedResources.filter((resource) =>
            failedKeys.has(`${resource.resourceType}:${resource.resourceId}`),
        );
        const selectedSnapshotsToRestore = selectedRemovedResources.filter((resource) =>
            failedKeys.has(`${resource.resourceType}:${resource.resourceId}`),
        );
        const removedCount = uniqueResources.length - failedResources.length;

        results.forEach((result) => {
            if (result.status === 'rejected') {
                captureException(result.reason);
            }
        });

        if (listedSnapshotsToRestore.length || selectedSnapshotsToRestore.length) {
            set((state) => {
                const folderIndex = state.folders.findIndex((folder) => folder.id === folderId);

                if (folderIndex !== -1 && listedSnapshotsToRestore.length) {
                    Object.assign(
                        state.folders[folderIndex],
                        restoreFolderResources(state.folders[folderIndex] as Folder, listedSnapshotsToRestore),
                    );
                }

                if (state.selectedFolder?.id === folderId && selectedSnapshotsToRestore.length) {
                    Object.assign(
                        state.selectedFolder,
                        restoreFolderResources(state.selectedFolder as Folder, selectedSnapshotsToRestore),
                    );
                }

                state.error = 'Failed to remove some resources from folder';
            });
        }

        if (removedCount) {
            const removedTitles = uniqueResources
                .filter((_, index) => results[index].status === 'fulfilled')
                .map((resource) => resource.itemTitle)
                .filter(Boolean);

            toast(removedCount === 1 ? 'Removed from Folder' : 'Resources Removed from Folder', {
                icon: createElement(TbRowRemove, { size: '1rem' }),
                description:
                    removedCount === 1 && removedTitles[0]
                        ? removedTitles[0]
                        : `${removedCount} resource${removedCount === 1 ? '' : 's'} removed.`,
            });
        }

        if (failedResources.length) {
            toast.error('Unable to Remove Some Resources', {
                description: `${failedResources.length} resource${failedResources.length === 1 ? '' : 's'} restored to the folder.`,
            });
        }
    },
});
